import { useMemo } from 'react';

import {
  Button,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  DateFormat,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  Modal,
  UserAvatarCard,
  UserIconNoBorder,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import { getAgeFromDOB, getPreferredFullName } from '~/lib/util/patient.util';
import { Path } from '~/routes/constants';

const { h6, paragraph, label } = EquipUITheme.typography;
const {
  neutral: { secondary: neutralSecondary },
} = EquipUIFireflyV2Theme.colors;

type SupportLinkedAccountsModalProps = {
  isOpen: boolean;
  linkedPatients: SupportOtherLinkedPatient[];
  modalId: string;
  onClose: () => void;
  supportEmail?: string;
};

const TOP_LEVEL_ID = 'support-linked-accounts-modal';

interface LinkedAccountCardProps {
  onClick: () => void;
  patient: SupportOtherLinkedPatient;
}

const LinkedAccountCard = ({ patient, onClick }: LinkedAccountCardProps) => {
  const { patientExternalId, chosenName, firstName, lastName, dateOfBirth } =
    patient;

  return (
    <ChakraFlex
      alignItems="center"
      bg="neutral.background.secondary"
      borderRadius="16px"
      justifyContent="space-between"
      marginBottom="24px"
      padding="24px"
    >
      <UserAvatarCard
        avatarSize="large"
        id={`${TOP_LEVEL_ID}__patient-${patientExternalId}`}
        primaryText={[getPreferredFullName(chosenName, firstName, lastName)]}
        primaryTextBold
        secondaryText={
          dateOfBirth
            ? [
                `${DateTime.fromFormat(
                  dateOfBirth,
                  DateFormat.shortYearMonthDay,
                ).toFormat(DateFormat.longDate)} - ${getAgeFromDOB(
                  dateOfBirth,
                  'yyyy-LL-dd',
                )} years old`,
              ]
            : ['unknown']
        }
      />

      <ChakraBox
        as="button"
        bg="neutral.background.secondary"
        borderColor="neutral.line"
        borderRadius="8px"
        borderWidth="1px"
        id={`${TOP_LEVEL_ID}__patient-profile__${patientExternalId}`}
        onClick={onClick}
        padding="7px"
      >
        <UserIconNoBorder fill={neutralSecondary} />
      </ChakraBox>
    </ChakraFlex>
  );
};

interface LinkedAccountListProps {
  onPatientClick: (patient: SupportOtherLinkedPatient) => void;
  patients: SupportOtherLinkedPatient[];
  title: string;
}

const LinkedAccountList = ({
  onPatientClick,
  patients,
  title,
}: LinkedAccountListProps) => {
  if (patients.length === 0) {
    return null;
  }

  return (
    <>
      <ChakraText {...label} color="neutral.secondary" margin="32px 0 16px">
        {title}
      </ChakraText>
      {patients.map((patient) => {
        return (
          <LinkedAccountCard
            key={`${TOP_LEVEL_ID}__patient-${patient.patientExternalId}`}
            onClick={() => onPatientClick(patient)}
            patient={patient}
          />
        );
      })}
    </>
  );
};

const SupportLinkedAccountsModal = ({
  isOpen,
  onClose,
  linkedPatients,
  supportEmail,
  modalId,
}: SupportLinkedAccountsModalProps) => {
  const linkedAccountsCount = linkedPatients ? linkedPatients.length : 0;

  const proxyAccessPatients = useMemo(() => {
    return linkedPatients.filter((support) => support.isProxy === true);
  }, [linkedPatients]);

  const generalAccessPatients = useMemo(() => {
    return linkedPatients.filter((support) => support.isProxy === false);
  }, [linkedPatients]);

  const handlePatientClick = (patient: SupportOtherLinkedPatient) => {
    window.open(
      `${Path.MAUD_PATIENTS}/${patient.patientExternalId}`,
      '_blank',
      'noopener',
    );
  };

  return (
    <Modal
      footerContent={
        <>
          <Button
            id={`${TOP_LEVEL_ID}__confirm`}
            isFullWidth
            margin="0 0 0 16px"
            onClick={onClose}
            type="submit"
          >
            Close
          </Button>
        </>
      }
      id={`${modalId}_linked_patients`}
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      title={
        <ChakraText
          {...h6}
          color="neutral.secondary"
        >{`This email has access to ${linkedAccountsCount} patient app accounts`}</ChakraText>
      }
    >
      <ChakraBox marginBottom="24px" marginTop="24px">
        <ChakraText {...paragraph} color="neutral.secondary">
          <strong>{supportEmail}</strong>
          {` currently has access to ${linkedAccountsCount} patient app accounts.`}
        </ChakraText>

        <LinkedAccountList
          onPatientClick={(patient) => handlePatientClick(patient)}
          patients={proxyAccessPatients}
          title="Proxy access"
        />
        <LinkedAccountList
          onPatientClick={(patient) => handlePatientClick(patient)}
          patients={generalAccessPatients}
          title="General access"
        />
      </ChakraBox>
    </Modal>
  );
};

export default SupportLinkedAccountsModal;
